import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Contact from './app/Navigation/Contact';
import KoreanToGo from './app/Navigation/KoreanToGo';
import OurValues from './app/Navigation/OurValues';
import WhereToFind from './app/Navigation/WhereToFind';
import { useProduct } from '../hooks/useProduct';
import { useLanguage } from '../hooks/useLanguage';
import { type ReactNode, useEffect, useRef, useState } from 'react';

interface NavItem {
  id: string;
  label: {
    en: ReactNode;
    fr: ReactNode;
  };
}

const navItems: NavItem[] = [
  {
    id: 'values',
    label: {
      en: 'OUR VALUES',
      fr: 'NOS VALEURS',
    },
  },
  {
    id: 'find',
    label: {
      en: 'WHERE TO FIND',
      fr: 'NOUS TROUVER',
    },
  },
  {
    id: 'togo',
    label: {
      en: (
        <span>
          THE SEOUL ORIGINAL
          <span className="align-super ml-[0.1em] text-[0.6em] leading-none">
            TM
          </span>
        </span>
      ),
      fr: (
        <span>
          THE SEOUL ORIGINAL
          <span className="align-super ml-[0.1em] text-[0.6em] leading-none">
            TM
          </span>
        </span>
      ),
    },
  },
  {
    id: 'contact',
    label: {
      en: 'CONTACT',
      fr: 'CONTACT',
    },
  },
];

export default function Navigation() {
  const icons = [
    `${import.meta.env.VITE_CLOUDFRONT_URL}/draps_1.png`,
    `${import.meta.env.VITE_CLOUDFRONT_URL}/draps_2.png`,
    `${import.meta.env.VITE_CLOUDFRONT_URL}/draps_3.png`,
  ];
  const [iconIndex, setIconIndex] = useState(0);
  const {
    activeNavPanel,
    setActiveNavPanel,
    activeProductId,
    setActiveProductId,
  } = useProduct();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setIconIndex((prev) => (prev + 1) % icons.length);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [icons.length]);

  // References to scroll containers
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when active panel changes
  useEffect(() => {
    if (activeNavPanel) {
      // Reset scroll position for desktop panel
      if (desktopPanelRef.current) {
        desktopPanelRef.current.scrollTop = 0;
      }
      // Reset scroll position for mobile panel
      if (mobilePanelRef.current) {
        mobilePanelRef.current.scrollTop = 0;
      }
    }
  }, [activeNavPanel]);

  const handleNavClick = (id: string) => {
    if (activeNavPanel === id) {
      setActiveNavPanel(null);
    } else {
      // If product panel is open, close it first with delay
      if (activeProductId) {
        setActiveProductId(null);
        setTimeout(() => {
          setActiveNavPanel(id);
        }, 300); // Wait for product panel to close
      } else {
        setActiveNavPanel(id);
      }
    }
  };

  const renderPanelContent = () => {
    switch (activeNavPanel) {
      case 'values':
        return <OurValues />;
      case 'find':
        return <WhereToFind />;
      case 'togo':
        return <KoreanToGo />;
      case 'contact':
        return <Contact />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Desktop Navigation - Slides right when panel opens */}
      <motion.nav
        initial={false}
        animate={{ left: activeNavPanel ? '40%' : 0 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="hidden lg:flex fixed top-0 h-screen w-[5.8vw] bg-white border-r border-gray-200 flex-col items-center py-[1.5vh] z-50 shadow-sm"
      >
        {/* Icon at top - changes to close button when panel is open */}
        <div
          className="flex-shrink-0 cursor-pointer"
          onClick={() => {
            if (activeNavPanel) {
              setActiveNavPanel(null);
            } else if (activeProductId) {
              // Close product panel when clicking icon
              setActiveProductId(null);
            }
          }}
        >
          {activeNavPanel ? (
            <div className="w-[4.2vw] h-[4.2vw] flex items-center justify-center">
              <X className="w-[2.9vw] h-[2.9vw] text-red-600 hover:text-red-700 transition-colors" />
            </div>
          ) : (
            <img
              src={icons[iconIndex]}
              alt="Icon"
              className="w-[5vw] h-[5vw] object-contain"
            />
          )}
        </div>

        {/* Spacer to push navigation items down */}
        <div className="flex-1" />

        {/* Navigation items - rotated text at bottom */}
        <div className="flex flex-col items-center justify-end gap-[2.5vh] pb-[1.5vh]">
          {navItems.map((item) => (
            <motion.button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`relative transition-colors duration-300 ${
                activeNavPanel === item.id
                  ? 'text-red-600'
                  : 'text-gray-700 hover:text-red-600'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.8 }}
            >
              <span
                className="text-[0.8rem] font-extrabold tracking-wider whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              >
                {item.label[language]}
              </span>
            </motion.button>
          ))}

          {/* Language Switcher */}
          <div className="flex justify-center items-center gap-[0.6vw] pt-[1.2vh] border-t border-gray-200 w-[3.3vw]">
            <motion.button
              onClick={() => setLanguage('en')}
              className={`text-[0.73vw] font-bold transition-colors duration-300 ${
                language === 'en'
                  ? 'text-red-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              EN
            </motion.button>
            <motion.button
              onClick={() => setLanguage('fr')}
              className={`text-[0.73vw] font-bold transition-colors duration-300 ${
                language === 'fr'
                  ? 'text-red-600'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              FR
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Desktop Slide Panel - from left to right */}
      <AnimatePresence>
        {activeNavPanel && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            ref={desktopPanelRef}
            className="hidden lg:block fixed left-0 top-0 h-screen w-[40%] bg-white border-r border-gray-200 z-40 shadow-lg overflow-y-auto [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-red-100 [&::-webkit-scrollbar-thumb]:bg-red-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-red-700"
          >
            {renderPanelContent()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile/Tablet Navigation - Bottom */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-200 flex items-center z-50 shadow-lg">
        {navItems.map((item) => (
          <motion.button
            key={item.id}
            onClick={() => handleNavClick(item.id)}
            className={`flex-1 h-full flex items-center justify-center transition-colors duration-300 p-2 ${
              activeNavPanel === item.id
                ? 'text-red-600'
                : 'text-gray-700 hover:text-red-600'
            }`}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-[0.8rem] font-extrabold tracking-wide text-center leading-tight px-0.5">
              {item.label[language]}
            </span>
          </motion.button>
        ))}
      </nav>

      {/* Mobile Slide Panel - from bottom to top, covers entire screen */}
      <AnimatePresence>
        {activeNavPanel && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            ref={mobilePanelRef}
            className="lg:hidden fixed top-0 left-0 right-0 bottom-0 bg-white z-40 overflow-y-auto [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-red-100 [&::-webkit-scrollbar-thumb]:bg-red-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-red-700"
          >
            {renderPanelContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
